import { useCallback, useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { inputCls, labelCls, StyledSelect, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { SectionHeader } from "@/components/cccd/SectionHeader"
import { slugify } from "@/lib/slugify"
import { COUNTRIES } from "@alascorporation/utils"
import { LoadState } from "@/components/ui/LoadState"

type Sector = "public" | "private"

type AcademicInstitutionForm = {
  name: string
  slug: string
  sector: Sector | ""
  city: string
  country: string
}

const initialForm: AcademicInstitutionForm = {
  name: "",
  slug: "",
  sector: "",
  city: "",
  country: "",
}

export default function AcademicInstitutionFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<AcademicInstitutionForm>(initialForm)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const res = await api.get("/academic-institutions/admin?limit=200&page=1")
      const found = (res.data?.data?.institutions ?? []).find((i: { id: string }) => i.id === id)
      if (!found) {
        setError("Institución educativa no encontrada")
        return
      }
      setForm({
        name: found.name,
        slug: found.slug,
        sector: found.sector ?? "",
        city: found.city ?? "",
        country: found.country ?? "",
      })
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  function goBack() {
    navigate("/cccd/academic-institutions")
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      if (isEdit && id) {
        await api.put(`/academic-institutions/admin/${id}`, {
          name: form.name,
          sector: form.sector || null,
          city: form.city || null,
          country: form.country || null,
        })
      } else {
        await api.post("/academic-institutions/admin", {
          name: form.name,
          slug: slugify(form.name),
          sector: form.sector || undefined,
          city: form.city || undefined,
          country: form.country || undefined,
        })
      }
      qc.invalidateQueries({ queryKey: ["server-table", "/academic-institutions/admin"] })
      goBack()
    } catch (err) {
      setError(getErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        { label: "CCCD", to: "/cccd" },
        { label: "Instituciones educativas", to: "/cccd/academic-institutions" },
        isEdit ? "Editar" : "Nueva",
      ]}
      title={isEdit ? "Editar institución educativa" : "Nueva institución educativa"}
      subtitle="De dónde vienen los integrantes de un equipo CCCD o las personas certificadas Tripoli"
      tabs={[]}
      metrics={[]}
    >
      {loading ? (
        <LoadState />
      ) : (
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <SectionHeader
              title="Identidad"
              subtitle="Nombre de la institución y su slug autogenerado"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>}
              className="space-y-3"
            >
              <div>
                <label className={labelCls}>Nombre de la institución *</label>
                <input
                  required
                  autoFocus
                  placeholder="Nombre de la institución"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      name: e.target.value,
                      slug: isEdit ? f.slug : slugify(e.target.value),
                    }))
                  }
                  className={inputCls}
                />
              </div>
              <p className="rounded-lg bg-gray-50 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 dark:bg-gray-800">
                Slug: <span className="font-mono">{form.slug || "—"}</span> (autogenerado, no editable)
              </p>
            </SectionHeader>

            <SectionHeader
              title="Clasificación y ubicación"
              subtitle="Sector y localización geográfica de la institución"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 21s7-6.5 7-11.5A7 7 0 0 0 5 9.5C5 14.5 12 21 12 21Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><circle cx="12" cy="9.5" r="2.3" stroke="currentColor" strokeWidth="1.5" /></svg>}
              className="space-y-3 lg:col-span-2"
            >
              <div>
                <label className={labelCls}>Sector</label>
                <StyledSelect
                  value={form.sector}
                  onChange={(e) => setForm((f) => ({ ...f, sector: e.target.value as Sector | "" }))}
                >
                  <option value="">Sin clasificar</option>
                  <option value="public">Pública</option>
                  <option value="private">Privada</option>
                </StyledSelect>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Ciudad</label>
                  <input
                    placeholder="Ciudad"
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>País</label>
                  <StyledSelect
                    value={COUNTRIES.find((c) => c.label === form.country)?.value ?? ""}
                    onChange={(e) => {
                      const country = COUNTRIES.find((c) => c.value === e.target.value)
                      setForm((f) => ({ ...f, country: country?.label ?? "" }))
                    }}
                  >
                    <option value="">Selecciona...</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </StyledSelect>
                </div>
              </div>
            </SectionHeader>
          </div>

          {error ? (
            <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className={btnSecondary}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} className={btnSave}>
              {saving ? "Guardando..." : isEdit ? "Guardar" : "Crear"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
